import { useSelector } from "react-redux";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getStudentProfileById } from "../../../services/StudentProfileService";
import { getUserById, uploadUserAvatar } from "../../../services/UserService";
import { getClassById } from "../../../services/ClassService";
import { getLevelById } from "../../../services/LevelService";
import { getExams } from "../../../services/ExamService";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getResults } from "../../../services/ExamResultService";
import { getEnrollments } from "../../../services/EnrollmentService";
import AnnualTranscriptCard from "./AnnualTranscriptCard";
import { useTranslation } from "react-i18next";
import { usePermissions, canReadAnyTranscript } from "../../../utils/permissions";
import { getLocale, lazyLabels } from "../../../utils/i18nLabels";

/*
 * Styles responsive de la fiche étudiant.
 * - Écran >= 1200px : la carte d'identité reste collée sous le header
 *   (position: sticky natif, remplace le plugin theiaStickySidebar).
 * - Écran < 1200px : les colonnes s'empilent, rien n'est sticky.
 * Ajuste --student-sticky-top à la hauteur réelle de ton header.
 */
const responsiveStyles = `
.student-detail-page { --student-sticky-top: 80px; }
.student-detail-page .student-main-col,
.student-detail-page .student-side-col { min-width: 0; }
.student-detail-page .student-exam-table { overflow-x: auto; }

@media (min-width: 1200px) {
  .student-detail-page .student-sticky-col {
    position: sticky;
    top: var(--student-sticky-top);
    max-height: calc(100vh - var(--student-sticky-top) - 20px);
    overflow-y: auto;
  }
}

@media (max-width: 1199.98px) {
  .student-detail-page .student-sticky-col {
    position: static;
    max-height: none;
    overflow: visible;
  }
}

@media (max-width: 575.98px) {
  .student-detail-page .student-session-item {
    flex-direction: column;
    align-items: flex-start !important;
    row-gap: 0.5rem;
  }
}
`;

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(getLocale(), {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString(getLocale(), {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const sessionStatusLabels = lazyLabels({ scheduled: "common.examSessionStatus.scheduled", ongoing: "common.examSessionStatus.ongoing", closed: "common.examSessionStatus.closed", cancelled: "common.examSessionStatus.cancelled" });
const sessionStatusBadge = {
  scheduled: "info",
  ongoing: "primary",
  closed: "success",
  cancelled: "danger",
};

const StudentDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [classItem, setClassItem] = useState(null);
  const [levelItem, setLevelItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [exams, setExams] = useState([]);
  const [examSessions, setExamSessions] = useState([]);
  const [results, setResults] = useState([]);
  const [examDataLoading, setExamDataLoading] = useState(false);
  const [examDataError, setExamDataError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await getStudentProfileById(id);
        const studentProfile = res.data?.data;
        if (cancelled) return;
        setProfile(studentProfile);

        const [userRes, classRes, levelRes] = await Promise.all([
          studentProfile?.user_id
            ? getUserById(studentProfile.user_id).catch(() => null)
            : null,
          studentProfile?.class_id
            ? getClassById(studentProfile.class_id).catch(() => null)
            : null,
          studentProfile?.level_id
            ? getLevelById(studentProfile.level_id).catch(() => null)
            : null,
        ]);
        if (cancelled) return;
        setAccount(userRes?.data?.data || null);
        setClassItem(classRes?.data?.data || null);
        setLevelItem(levelRes?.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err?.response?.data?.message ||
              t("pages.studentDetail.impossibleChargerDetailsEtudiant"),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!profile) return undefined;
    let cancelled = false;

    const loadExamData = async () => {
      setExamDataLoading(true);
      setExamDataError("");
      try {
        const enrollmentsRes = await getEnrollments({
          page: 1,
          limit: 1,
          student_id: profile.id,
          status: "active",
        }).catch(() => null);
        const enrollment = enrollmentsRes?.data?.data?.[0] || null;
        const classId = enrollment?.class_id || profile.class_id;
        const groupId = enrollment?.student_group_id || null;

        const [examsRes, sessionsRes, resultsRes] = await Promise.all([
          getExams({ page: 1, limit: 200 }),
          classId
            ? getExamSessions({
                page: 1,
                limit: 20,
                class_id: classId,
                sort_by: "start_at",
                sort_dir: "desc",
              })
            : Promise.resolve(null),
          getResults({
            page: 1,
            limit: 100,
            student_id: profile.id,
            sort_by: "corrected_at",
            sort_dir: "desc",
          }),
        ]);
        if (cancelled) return;
        setExams(examsRes.data?.data || []);
        const relevantSessions = (sessionsRes?.data?.data || []).filter(
          (session) =>
            !session.student_group_id || session.student_group_id === groupId,
        );
        setExamSessions(relevantSessions.slice(0, 5));
        setResults(resultsRes.data?.data || []);
      } catch (err) {
        if (!cancelled) {
          setExamDataError(
            err?.response?.data?.message ||
              t("pages.studentDetail.impossibleChargerDonneesExamen"),
          );
        }
      } finally {
        if (!cancelled) setExamDataLoading(false);
      }
    };

    loadExamData();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const name =
    [account?.first_name, account?.last_name].filter(Boolean).join(" ") ||
    account?.email ||
    "-";
  const inactive = account?.is_active === false;

  // Photo de profil : seul un compte disposant de USER_UPDATE peut la
  // modifier depuis la fiche (mêmes règles que le back : permit('USER_UPDATE')
  // sur POST /users/:id/avatar).
  const canEditAvatar = usePermissions()("USER_UPDATE");
  const currentUser = useSelector((state) => state.auth.auth?.user);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const avatarInputRef = useRef(null);

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !account?.id) return;

    setAvatarError("");
    setAvatarUploading(true);
    try {
      const res = await uploadUserAvatar(account.id, file);
      const updated = res.data?.data;
      setAccount((prev) => ({
        ...prev,
        avatar: updated?.avatar,
        avatar_url: updated?.avatar_url,
      }));
    } catch (err) {
      setAvatarError(
        err?.response?.data?.message || t("header.avatar.error"),
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  const examLabel = (examId) =>
    exams.find((e) => e.id === examId)?.title || "-";

  const examStats = useMemo(() => {
    if (results.length === 0) return null;
    const scores = results.map((r) => Number(r.score_percentage) || 0);
    const passedCount = results.filter((r) => r.passed).length;
    return {
      total: results.length,
      average: (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1),
      passRate: Math.round((passedCount / results.length) * 100),
      best: Math.max(...scores).toFixed(1),
    };
  }, [results]);

  return (
    <div className="page-wrapper student-detail-page">
      <style>{responsiveStyles}</style>
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/student">{t("menu.students.title")}</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {t("pages.studentDetail.breadcrumb")}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("common.crud.loading")}</span>
            </div>
          </div>
        ) : loadError ? (
          <div className="alert alert-danger">{loadError}</div>
        ) : !profile ? (
          <div className="alert alert-warning">{t("pages.studentDetail.notFound")}</div>
        ) : (
          // align-items-start : la colonne de gauche ne s'étire plus à la
          // hauteur de la colonne de droite (plus de grand vide).
          <div className="row align-items-start">
            {/* Student Information */}
            <div className="col-xxl-4 col-xl-5 col-12 student-side-col">
              <div className="student-sticky-col">
                <div className="card border-white">
                  <div className="card-header">
                    <div className="d-flex align-items-center flex-wrap row-gap-3">
                      <div className="position-relative me-2 flex-shrink-0">
                        <div
                          className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed text-dark"
                          style={{ opacity: avatarUploading ? 0.5 : 1 }}
                        >
                          {account?.avatar_url ? (
                            <img
                              src={account.avatar_url}
                              className="img-fluid rounded-circle"
                              alt={name}
                            />
                          ) : (
                            <span className="fw-semibold fs-24">
                              {name.slice(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                        {canEditAvatar && (
                          <>
                            <button
                              type="button"
                              title={t("header.avatar.changePhoto")}
                              onClick={handleAvatarClick}
                              disabled={avatarUploading}
                              className="position-absolute bottom-0 end-0 rounded-circle bg-primary border border-white d-flex align-items-center justify-content-center p-0"
                              style={{ width: 22, height: 22 }}
                            >
                              <i className="ti ti-camera text-white fs-12"></i>
                            </button>
                            <input
                              ref={avatarInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="d-none"
                              onChange={handleAvatarChange}
                            />
                          </>
                        )}
                      </div>
                      <div className="overflow-hidden">
                        <span
                          className={`badge badge-soft-${inactive ? "danger" : "success"} d-inline-flex align-items-center mb-1`}
                        >
                          <i className="ti ti-circle-filled fs-5 me-1"></i>
                          {inactive ? t("common.crud.statusInactive") : t("common.crud.statusActive")}
                        </span>
                        <h5 className="mb-1 text-truncate">{name}</h5>
                        <p className="text-primary">{profile.matricule || "-"}</p>
                        {avatarError && (
                          <p className="text-danger small mb-0">{avatarError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Basic Information */}
                  <div className="card-body">
                    <h5 className="mb-3">{t("common.ui.informationsGenerales")}</h5>
                    <dl className="row mb-0">
                      <dt className="col-6 fw-medium text-dark mb-3">
                        {t("common.fields.matricule")}
                      </dt>
                      <dd className="col-6 mb-3 text-break">{profile.matricule || "-"}</dd>
                      <dt className="col-6 fw-medium text-dark mb-3">{t("common.fields.class")}</dt>
                      <dd className="col-6 mb-3 text-break">{classItem?.name || "-"}</dd>
                      <dt className="col-6 fw-medium text-dark mb-3">{t("common.fields.level")}</dt>
                      <dd className="col-6 mb-3 text-break">{levelItem?.name || "-"}</dd>
                      <dt className="col-6 fw-medium text-dark mb-3">
                        {t("common.ui.statutCompte")}
                      </dt>
                      <dd className="col-6 mb-3">
                        {inactive ? t("common.crud.statusInactive") : t("common.crud.statusActive")}
                      </dd>
                      <dt className="col-6 fw-medium text-dark mb-3">
                        {t("common.crud.enrolledOn")}
                      </dt>
                      <dd className="col-6 mb-3">
                        {formatDate(profile.created_at)}
                      </dd>
                    </dl>
                    <hr />
                    <h5 className="mb-3">{t("common.ui.coordonnees")}</h5>
                    <div className="d-flex align-items-center mb-3">
                      <span className="d-flex flex-column align-items-center justify-content-center avatar avatar-md bg-light rounded me-2 flex-shrink-0 text-default">
                        <i className="ti ti-phone"></i>
                      </span>
                      <div className="overflow-hidden">
                        <span className="text-dark fw-medium mb-1">
                          {t("common.fields.phone")}
                        </span>
                        <p className="text-break">{account?.phone || "-"}</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="d-flex flex-column align-items-center justify-content-center avatar avatar-md bg-light rounded me-2 flex-shrink-0 text-default">
                        <i className="ti ti-mail"></i>
                      </span>
                      <div className="overflow-hidden">
                        <span className="text-dark fw-medium mb-1">
                          {t("common.ui.adresseEMail")}
                        </span>
                        <p className="text-break">{account?.email || "-"}</p>
                      </div>
                    </div>
                  </div>
                  {/* /Basic Information */}
                </div>
              </div>
            </div>
            {/* /Student Information */}

            <div className="col-xxl-8 col-xl-7 col-12 student-main-col">
              {canReadAnyTranscript(currentUser) && <AnnualTranscriptCard studentId={profile?.id || id} />}
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">{t("pages.studentDetail.examsOfStudent")}</h5>
                </div>
                <div className="card-body">
                  {examDataError && (
                    <div className="alert alert-warning">{examDataError}</div>
                  )}

                  <h6 className="mb-3">{t("menu.exams.sessions")}</h6>
                  {examDataLoading ? (
                    <div className="text-center py-3">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">{t("common.crud.loading")}</span>
                      </div>
                    </div>
                  ) : examSessions.length > 0 ? (
                    <div className="list-group list-group-flush mb-4">
                      {examSessions.map((session) => (
                        <div
                          key={session.id}
                          className="list-group-item d-flex justify-content-between align-items-center px-0 student-session-item"
                        >
                          <div>
                            <p className="mb-0 fw-medium">
                              {examLabel(session.exam_id)}
                            </p>
                            <small className="text-muted">
                              {new Date(session.start_at).toLocaleString(
                                getLocale(),
                              )}{" "}
                              →{" "}
                              {new Date(session.end_at).toLocaleString(getLocale())}
                            </small>
                          </div>
                          <span
                            className={`badge badge-soft-${sessionStatusBadge[session.status] || "secondary"}`}
                          >
                            {sessionStatusLabels[session.status] ||
                              session.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">
                      {t("pages.studentDetail.noSessionsForStudent")}
                    </p>
                  )}

                  <hr />

                  <h6 className="mb-3 mt-3">{t("menu.results.results")}</h6>
                  {examDataLoading ? null : results.length > 0 ? (
                    <div className="table-responsive student-exam-table mb-2">
                      <table className="table table-sm mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>{t("common.ui.examen")}</th>
                            <th>{t("common.ui.score")}</th>
                            {/* <th>{t("common.ui.resultat")}</th> */}
                            <th>{t("common.ui.corrige")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.slice(0, 5).map((result) => (
                            <tr key={result.id}>
                              <td>{examLabel(result.exam_id)}</td>
                              <td>{result.score_percentage}%</td>
                              {/* <td>
                                <span
                                  className={`badge badge-soft-${result.passed ? "success" : "danger"}`}
                                >
                                  {result.passed ? t("common.ui.admis") : t("common.ui.nonAdmis")}
                                </span>
                              </td> */}
                              <td>
                                {result.corrected_at
                                  ? formatDate(result.corrected_at)
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">
                      {t("pages.studentDetail.noResultsForStudent")}
                    </p>
                  )}

                  <hr />

                  <h6 className="mb-3 mt-3">{t("menu.statistics")}</h6>
                  <div className="row">
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-clipboard-check text-primary fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("pages.studentDetail.correctedExams")}</p>
                          <h5 className="mb-0">
                            {examStats ? examStats.total : "-"}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-info-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-chart-bar text-info fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("common.ui.scoreMoyen")}</p>
                          <h5 className="mb-0">
                            {examStats ? `${examStats.average}%` : "-"}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-success-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-chart-pie text-success fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("common.ui.tauxReussite")}</p>
                          <h5 className="mb-0">
                            {examStats ? `${examStats.passRate}%` : "-"}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3 mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-lg bg-warning-transparent rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                          <i className="ti ti-trophy text-warning fs-20"></i>
                        </span>
                        <div>
                          <p className="mb-0 text-muted">{t("pages.studentDetail.bestScore")}</p>
                          <h5 className="mb-0">
                            {examStats ? `${examStats.best}%` : "-"}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDetail;